import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, TextField, Menu, MenuItem, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { countries, getEmojiFlag, TCountryCode } from "countries-list";
import * as Flags from "country-flag-icons/react/3x2";
import { PHONE_INPUT_MAX_DIGITS } from "../utils/phoneValidation";

/**
 * A phone-number input with an inline country-code selector, mirroring the one used in
 * the Edit Profile (settings) and Admin onboarding screens.
 *
 * Unlike those screens — which keep a *separate* country field and store only the national
 * digits — the forms that consume this component (Add Instructor, Add Student) have no
 * dedicated country field. So this component is self-contained: it owns the selected country
 * and stores the combined value `"+<dialCode> <national>"` back into the single phone field
 * the form already has. The backend stores phone as a free string and its validation regex
 * already permits a leading `+` and spaces, so no schema/backend change is required.
 *
 * Round-tripping:
 *  - On load it parses an incoming `"+<dialCode> <national>"` value back into a country + number.
 *  - Legacy values stored without a country code (just digits) are shown as-is with the
 *    "Code" placeholder and are left untouched unless the user edits them — so opening an
 *    existing record and saving without touching the phone never mutates the stored value.
 */


type CountryOption = {
  code: TCountryCode;
  name: string;
  emoji: string;
  dialCode: string;
};

// Built once: a pure transform of the static country list.
const COUNTRY_OPTIONS: CountryOption[] = Object.entries(countries)
  .map(([code, country]) => ({
    code: code as TCountryCode,
    name: country.name,
    emoji: getEmojiFlag(code as TCountryCode),
    dialCode: String(country.phone?.[0] || ""),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Longest dial code first so prefix matching is unambiguous (e.g. so "+1" countries
// don't shadow longer codes that merely start with 1).
const COUNTRY_OPTIONS_BY_DIAL = [...COUNTRY_OPTIONS].sort(
  (a, b) => b.dialCode.length - a.dialCode.length,
);

const dialCodeOf = (countryName: string | null): string =>
  COUNTRY_OPTIONS.find((c) => c.name === countryName)?.dialCode || "";

/** Splits a stored value into a country (if a dial code is present) and the national digits. */
const parsePhone = (raw: string): { countryName: string | null; national: string } => {
  const value = (raw || "").trim();
  if (!value) return { countryName: null, national: "" };

  if (value.startsWith("+")) {
    // Preferred shape (what this component emits): "+<dialCode> <national>".
    const spaceIdx = value.indexOf(" ");
    if (spaceIdx > 0) {
      const code = value.slice(1, spaceIdx).replace(/\D/g, "");
      const rest = value.slice(spaceIdx + 1).replace(/\D/g, "");
      const match = COUNTRY_OPTIONS.find((c) => c.dialCode === code);
      if (match) return { countryName: match.name, national: rest };
    }
    // Fallback for "+<dialCode><national>" with no separator: longest-prefix match.
    const digits = value.replace(/\D/g, "");
    const match = COUNTRY_OPTIONS_BY_DIAL.find(
      (c) => c.dialCode && digits.startsWith(c.dialCode),
    );
    if (match) {
      return { countryName: match.name, national: digits.slice(match.dialCode.length) };
    }
    return { countryName: null, national: digits };
  }

  // Legacy value with no country code — keep the digits, leave the country unset.
  return { countryName: null, national: value.replace(/\D/g, "") };
};

/** Derives the national digits to display, given the value and the currently selected country. */
const nationalFromValue = (value: string, countryName: string | null): string => {
  const v = (value || "").trim();
  if (!v) return "";
  if (v.startsWith("+")) {
    const spaceIdx = v.indexOf(" ");
    if (spaceIdx > 0) return v.slice(spaceIdx + 1).replace(/\D/g, "");
    const digits = v.replace(/\D/g, "");
    const dial = dialCodeOf(countryName);
    if (dial && digits.startsWith(dial)) return digits.slice(dial.length);
    return digits;
  }
  return v.replace(/\D/g, "");
};

/** Combines a country + national digits back into the stored string. */
const combine = (countryName: string | null, national: string): string => {
  const digits = (national || "").replace(/\D/g, "");
  if (!digits) return ""; // empty stays empty so optional fields remain optional
  const dial = dialCodeOf(countryName);
  return dial ? `+${dial} ${digits}` : digits;
};

type PhoneNumberFieldProps = {
  value: string;
  onChange: (combined: string) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Country preselected when the field starts empty. Defaults to India (app convention). */
  defaultCountryName?: string;
};

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  onBlur,
  error = false,
  helperText,
  disabled = false,
  placeholder = "Enter phone number",
  defaultCountryName = "India",
}) => {
  const [countryName, setCountryName] = useState<string | null>(() => {
    if (!value || !value.trim()) return defaultCountryName;
    return parsePhone(value).countryName; // null for legacy un-coded numbers
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");

  // Tracks the value we last emitted so external updates (e.g. an async edit-mode load)
  // re-sync the country without us reacting to our own echo.
  const lastEmitted = useRef<string>(value);

  useEffect(() => {
    if (value === lastEmitted.current) return;
    // External change (e.g. an edit-mode record loading in). Reflect exactly what the
    // stored value implies: its country if it carries a dial code, otherwise "none"
    // (shown as "Code") so we never silently assume a code for a legacy number — the
    // user must pick one before any prefix is applied.
    lastEmitted.current = value;
    if (!value || !value.trim()) {
      setCountryName(defaultCountryName);
      return;
    }
    setCountryName(parsePhone(value).countryName);
  }, [value, defaultCountryName]);

  const national = useMemo(() => nationalFromValue(value, countryName), [value, countryName]);
  const dialCode = dialCodeOf(countryName);

  const selectedCountry = useMemo(
    () => COUNTRY_OPTIONS.find((c) => c.name === countryName) || null,
    [countryName],
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.dialCode.includes(query.replace("+", "")) ||
        c.code.toLowerCase().includes(query),
    );
  }, [search]);

  const emit = (nextCountry: string | null, nextNational: string) => {
    const combined = combine(nextCountry, nextNational);
    lastEmitted.current = combined;
    onChange(combined);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emit(countryName, e.target.value);
  };

  const handleCountrySelect = (name: string) => {
    setCountryName(name);
    setMenuAnchor(null);
    setSearch("");
    emit(name, national);
  };

  const Flag = selectedCountry ? (Flags as any)[selectedCountry.code] : null;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: 40,
          border: "1px solid #E0E0E0",
          borderColor: error ? "#d32f2f" : "#E0E0E0",
          borderRadius: "6px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          opacity: disabled ? 0.6 : 1,
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:focus-within": { borderColor: error ? "#d32f2f" : "#6CBC44" },
        }}
      >
        <Box
          onClick={(e) => {
            if (!disabled) setMenuAnchor(e.currentTarget);
          }}
          sx={{
            width: "fit-content",
            maxWidth: { xs: 110, sm: 122 },
            px: 1.25,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            borderRight: "1px solid #E4E7EC",
            cursor: disabled ? "default" : "pointer",
            flexShrink: 0,
            height: "100%",
          }}
        >
          <Box sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {Flag ? (
              <Flag style={{ width: 20, height: 14, borderRadius: "2px" }} />
            ) : (
              <Typography sx={{ fontSize: 13 }}>{selectedCountry?.emoji || "🌐"}</Typography>
            )}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: dialCode ? "#1D2939" : "#98A2B3" }}>
            {dialCode ? `+${dialCode}` : "Code"}
          </Typography>
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 17, color: "#98A2B3" }} />
        </Box>

        <TextField
          value={national}
          placeholder={placeholder}
          onChange={handleNationalChange}
          onBlur={onBlur}
          disabled={disabled}
          fullWidth
          variant="standard"
          inputProps={{ inputMode: "numeric", maxLength: PHONE_INPUT_MAX_DIGITS }}
          InputProps={{ disableUnderline: true }}
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            height: "100%",
            "& .MuiInputBase-root": {
              display: "flex",
              alignItems: "center",
              px: 2,
              width: "100%",
              height: "100%",
            },
            "& .MuiInputBase-input": {
              fontSize: { xs: "12px", sm: "14px" },
              color: "#1D2939",
              padding: 0,
              height: "100%",
              boxSizing: "border-box",
            },
          }}
        />
      </Box>

      {helperText ? (
        <Box
          sx={{
            fontSize: { xs: "9px", sm: "12px" },
            color: "#d32f2f",
            ml: 0.5,
            mt: "4px",
            fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
          }}
        >
          {helperText}
        </Box>
      ) : null}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setSearch("");
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            maxHeight: 380,
            borderRadius: "16px",
            border: "1px solid #D0D5DD",
            boxShadow: "0 20px 40px rgba(16, 24, 40, 0.12)",
            p: 0.5,
          },
        }}
        MenuListProps={{ disablePadding: true }}
      >
        <Box sx={{ p: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            fullWidth
            size="small"
            autoFocus
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            InputProps={{
              startAdornment: <SearchOutlinedIcon sx={{ color: "#98A2B3", mr: 1 }} />,
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 300, overflowY: "auto", pb: 0.5 }}>
          {filteredOptions.map((country) => {
            const OptionFlag = (Flags as any)[country.code];
            return (
              <MenuItem
                key={country.code}
                onClick={() => handleCountrySelect(country.name)}
                sx={{ py: 1.25, px: 1.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ width: 24, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {OptionFlag ? (
                      <OptionFlag style={{ width: 22, height: 16, borderRadius: "2px" }} />
                    ) : (
                      <span>{country.emoji}</span>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1D2939" }}>
                    {country.name}
                    {country.dialCode ? ` (+${country.dialCode})` : ""}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </Box>
  );
};

export default PhoneNumberField;
